import { getGetBoundariesUrl } from 'generatedTypes/flight/services/boundary-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { BoundaryResponse, FetchBoundaries, FetchBoundariesParams } from './types';

const useBoundaries = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchBoundaries: FetchBoundaries = async request => {
        try {
            setIsLoading(true);
            const url = getGetBoundariesUrl(request);

            return await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchBoundaries(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: boundariesData,
        fetch: paginatedFetchBoundaries,
        reset,
        totalCount
    } = usePagination<BoundaryResponse>({
        callback: fetchBoundaries
    });

    const handleFetchBoundaries = (args?: FetchBoundariesParams) => {
        const { includeUnassigned, name, pageSize = DEFAULT_PAGE_SIZE, siteId, sort } = args || {};

        return paginatedFetchBoundaries({
            includeUnassigned,
            name,
            pageSize,
            siteId,
            sort
        });
    };

    return {
        boundaries: boundariesData as BoundaryResponse[],
        fetch: handleFetchBoundaries,
        isLoading,
        reset,
        totalCount
    };
};

export default useBoundaries;
